import { useEffect, useRef, useState } from "react";
import { Controller, ControllerRenderProps, FieldPath, useForm } from "react-hook-form";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  Alert,
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import ButtonLoading from "../../Button/ButtonLoading";
import PageContentContainer from "../../Container/PageContentContainer";
import ContentBox from "../../ContentBox/ContentBox";
import PageHeadline from "../../PageHeadline/PageHeadline";
import {
  getManualCommandRuns,
  ManualCommandDefinition,
  ManualCommandParameterDefinition,
  ManualCommandRun,
  startManualCommand,
} from "../../../services/manualCommandService";
import commandCenterPageStyles from "./commandCenterPageStyles";
import { manualCommandDefinitions, manualCommands } from "./manualCommandDefinitions";

const ACTIVE_STATUSES: ManualCommandRun["status"][] = ["queued", "running"];

type CommandCenterFormValues = {
  commandKey: string;
  parameters: Record<string, string | number>;
};

function getStatusLabel(status: ManualCommandRun["status"] | null): string {
  switch (status) {
    case "queued":
      return "Wartet";
    case "running":
      return "Läuft";
    case "completed":
      return "Abgeschlossen";
    case "failed":
      return "Fehlgeschlagen";
    default:
      return "Noch nicht gestartet";
  }
}

function getStatusColor(status: ManualCommandRun["status"] | null): "default" | "info" | "success" | "error" {
  switch (status) {
    case "queued":
    case "running":
      return "info";
    case "completed":
      return "success";
    case "failed":
      return "error";
    default:
      return "default";
  }
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

function getCommandLabel(commandKey: string): string {
  return manualCommandDefinitions[commandKey]?.commandName ?? commandKey;
}

function getDefaultParameterValues(command: ManualCommandDefinition | undefined): Record<string, string | number> {
  const parameters: Record<string, string | number> = {};
  for (const parameter of command?.parameters ?? []) {
    if (parameter.defaultValue !== undefined) {
      parameters[parameter.name] = parameter.defaultValue;
    }
  }

  return parameters;
}

function getSubmittedParameterValues(parameters: Record<string, string | number>): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(parameters).filter(([, value]) => value !== "" && !(typeof value === "number" && Number.isNaN(value)))
  );
}

function formatRunDate(dateTime: string | null): string {
  if (dateTime === null) {
    return "";
  }

  const date = new Date(dateTime);
  if (Number.isNaN(date.getTime())) {
    return dateTime;
  }

  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function renderParameterField(
  parameter: ManualCommandParameterDefinition,
  field: ControllerRenderProps<CommandCenterFormValues, FieldPath<CommandCenterFormValues>>,
  errorMessage: string | undefined,
  disabled: boolean,
) {
  if (parameter.type === "select") {
    const labelId = `manual-command-parameter-${parameter.name}-label`;

    return (
      <FormControl fullWidth disabled={disabled} error={!!errorMessage}>
        <InputLabel id={labelId}>{parameter.label}</InputLabel>
        <Select
          label={parameter.label}
          labelId={labelId}
          name={field.name}
          onBlur={field.onBlur}
          onChange={(event: SelectChangeEvent<string>) => field.onChange(event.target.value)}
          value={String(field.value ?? parameter.defaultValue)}
        >
          {parameter.options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{errorMessage}</FormHelperText>
      </FormControl>
    );
  }

  return (
    <TextField
      disabled={disabled}
      error={!!errorMessage}
      fullWidth
      helperText={errorMessage}
      inputRef={field.ref}
      label={parameter.label}
      name={field.name}
      onBlur={field.onBlur}
      onChange={(event) => {
        if (parameter.type === "number") {
          field.onChange(event.target.value === "" ? "" : Number(event.target.value));
          return;
        }

        field.onChange(event.target.value);
      }}
      type={parameter.type === "number" ? "number" : "text"}
      value={field.value ?? parameter.defaultValue ?? ""}
    />
  );
}

export default function CommandCenterPage() {
  const [runs, setRuns] = useState<ManualCommandRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const outputRef = useRef<HTMLPreElement | null>(null);

  const formContext = useForm<CommandCenterFormValues>({
    defaultValues: {
      commandKey: manualCommands[0]?.key ?? "",
      parameters: getDefaultParameterValues(manualCommands[0]),
    },
  });
  const { clearErrors, control, handleSubmit, setValue, watch } = formContext;

  const selectedCommandKey = watch("commandKey");
  const activeRun = runs.find((run) => ACTIVE_STATUSES.includes(run.status)) ?? null;
  const selectedRun = selectedRunId !== null ? runs.find((run) => run.id === selectedRunId) ?? null : null;
  const isRunActive = activeRun !== null;
  const selectedCommand = manualCommands.find((command) => command.key === selectedCommandKey);
  const output = selectedRun?.output || selectedRun?.errorMessage || "Noch keine Ausgabe vorhanden.";

  useEffect(() => {
    setValue("parameters", getDefaultParameterValues(selectedCommand), {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    clearErrors("parameters");
  }, [selectedCommandKey, selectedCommand, setValue, clearErrors]);

  useEffect(() => {
    let isMounted = true;

    getManualCommandRuns()
      .then((loadedRuns) => {
        if (!isMounted) {
          return;
        }

        setRuns(loadedRuns);
        setSelectedRunId(loadedRuns.find((run) => ACTIVE_STATUSES.includes(run.status))?.id ?? null);
      })
      .catch((error) => {
        if (isMounted) {
          setErrorMessage(getErrorMessage(error, "Die letzten Läufe konnten nicht geladen werden."));
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isRunActive) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      getManualCommandRuns()
        .then((loadedRuns) => {
          setRuns(loadedRuns);
        })
        .catch((error) => {
          setErrorMessage(getErrorMessage(error, "Die letzten Läufe konnten nicht aktualisiert werden."));
        });
    }, 2000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isRunActive]);

  useEffect(() => {
    if (selectedRun === null || !ACTIVE_STATUSES.includes(selectedRun.status) || outputRef.current === null) {
      return;
    }

    outputRef.current.scrollTop = outputRef.current.scrollHeight;
  }, [selectedRun?.errorMessage, selectedRun?.output, selectedRun?.status]);

  async function handleStart(values: CommandCenterFormValues) {
    setErrorMessage(null);
    setIsStarting(true);

    try {
      const run = await startManualCommand(values.commandKey, getSubmittedParameterValues(values.parameters));
      setRuns((currentRuns) => [run, ...currentRuns.filter((currentRun) => currentRun.id !== run.id)].slice(0, 10));
      setSelectedRunId(run.id);
    } catch (error) {
      setErrorMessage(getErrorMessage(error, "Das Command konnte nicht gestartet werden."));
    } finally {
      setIsStarting(false);
    }
  }

  return (
    <PageContentContainer>
      <PageHeadline main="Kommandozentrale" />

      <ContentBox headline="Command ausführen">
        <form noValidate onSubmit={handleSubmit(handleStart)}>
          <Stack spacing={2}>
            <Controller
              control={control}
              name="commandKey"
              rules={{ required: "Bitte wählen Sie ein Command aus." }}
              render={({ field, fieldState }) => (
                <FormControl fullWidth disabled={isRunActive} error={!!fieldState.error}>
                  <InputLabel id="manual-command-select-label">Command</InputLabel>
                  <Select
                    {...field}
                    label="Command"
                    labelId="manual-command-select-label"
                    onChange={(event: SelectChangeEvent<string>) => field.onChange(event.target.value)}
                    value={field.value}
                  >
                    {manualCommands.map((command) => (
                      <MenuItem key={command.key} value={command.key}>
                        {command.commandName}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />

            {selectedCommand !== undefined && (
              <Stack spacing={0.5} sx={commandCenterPageStyles.descriptionBlock}>
                <Typography variant="subtitle2">{selectedCommand.label}</Typography>
                <Typography variant="body2">{selectedCommand.description}</Typography>
              </Stack>
            )}

            {selectedCommand?.parameters?.map((parameter) => (
              <Controller
                control={control}
                key={parameter.name}
                name={`parameters.${parameter.name}` as FieldPath<CommandCenterFormValues>}
                rules={{ required: parameter.required ? "Bitte geben Sie einen Wert ein." : false }}
                render={({ field, fieldState }) => renderParameterField(parameter, field, fieldState.error?.message, isRunActive)}
              />
            ))}

            {errorMessage !== null && <Alert severity="error">{errorMessage}</Alert>}

            <Stack direction="row" sx={commandCenterPageStyles.formActions}>
              {selectedRun !== null && (
                <Stack direction="row" sx={commandCenterPageStyles.statusLine}>
                  <Chip
                    color={getStatusColor(selectedRun.status)}
                    label={getStatusLabel(selectedRun.status)}
                    size="small"
                  />
                  <Typography variant="body2">Lauf #{selectedRun.id}</Typography>
                  {selectedRun.exitCode !== null && <Typography variant="body2">Exit-Code: {selectedRun.exitCode}</Typography>}
                </Stack>
              )}

              <ButtonLoading
                color="primary"
                disabled={!selectedCommandKey || isRunActive || manualCommands.length === 0}
                isLoading={isStarting}
                startIcon={!isStarting ? <PlayArrowIcon /> : undefined}
                type="submit"
                variant="contained"
              >
                Starten
              </ButtonLoading>
            </Stack>
          </Stack>
        </form>
      </ContentBox>

      <ContentBox headline="Letzte Läufe">
        {runs.length === 0 ? (
          <Typography variant="body2">Noch keine Läufe vorhanden.</Typography>
        ) : (
          <List disablePadding>
            {runs.map((run) => (
              <ListItemButton
                key={run.id}
                onClick={() => setSelectedRunId(run.id)}
                selected={selectedRunId === run.id}
                sx={commandCenterPageStyles.historyItem}
              >
                <ListItemText
                  primary={getCommandLabel(run.commandKey)}
                  secondary={`Lauf #${run.id} - ${formatRunDate(run.created)}`}
                  sx={commandCenterPageStyles.historyItemText}
                />
                <Chip color={getStatusColor(run.status)} label={getStatusLabel(run.status)} size="small" />
              </ListItemButton>
            ))}
          </List>
        )}
      </ContentBox>

      {selectedRun !== null && (
        <ContentBox headline="Ausgabe">
          <Box component="pre" ref={outputRef} sx={commandCenterPageStyles.outputWindow}>
            {output}
          </Box>
        </ContentBox>
      )}
    </PageContentContainer>
  );
}
